import { isHfdFef9DongleX87NeonIdentifier } from "../main/hfdFef9Dongle";
import { canonicalKeyboardTypeForRhythmPolicy } from "./globalRhythmKeyboardPolicy";
import {
  isM98GlobalRhythmSyncCapableEquipment,
  normalizeEquipmentName,
  resolveHuafendaConfigRowByNameAndWorkMode,
  resolveHuafendaKeyboardTypeFromNameBlob,
} from "./supportedKeyboardMatch";

/** 子应用 musicDance 专用 2.4G 型号（均走 setMusicDataV1 列高协议） */
export const HFD_MUSIC_RHYTHM_24G_KEYBOARD_TYPES = new Set([
  "75Pro",
  "75ProTFT",
  "M98",
  "DK108",
]);

/** 2.4G 走 setMusicDataV1 列高协议的型号（与 M98 同级，含 DK108） */
export const HFD_MUSIC_V1_KEYBOARD_TYPES = new Set([
  "75Pro",
  "75ProTFT",
  "M98",
  "DK108",
]);

const HFD_MUSIC_RHYTHM_DISPLAY_NAMES = new Set([
  "x75 pro",
  "x75 pro screen",
  "m98",
  "dk108",
]);

export type HfdRhythmEquipmentSnapshot = {
  keyboardType?: unknown;
  displayname?: unknown;
  displayName?: unknown;
  workMode?: unknown;
};

export function is24GWorkMode(workMode: string | number | undefined): boolean {
  return String(workMode) === "2" || workMode === 2;
}

function readWorkModeFromDeviceInitialStatus(): string | number | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem("deviceInitialStatus");
    if (!raw) return undefined;
    const d = JSON.parse(raw) as { workMode?: string | number };
    return d?.workMode;
  } catch {
    return undefined;
  }
}

export function resolveEquipmentWorkMode(
  equipment:
    | HfdRhythmEquipmentSnapshot
    | Record<string, unknown>
    | null
    | undefined
): string | number | undefined {
  if (
    equipment &&
    (equipment as HfdRhythmEquipmentSnapshot).workMode !== undefined &&
    (equipment as HfdRhythmEquipmentSnapshot).workMode !== null
  ) {
    return (equipment as HfdRhythmEquipmentSnapshot).workMode as
      | string
      | number;
  }
  return readWorkModeFromDeviceInitialStatus();
}

export function isHfdMusicRhythm24GKeyboardModel(
  keyboardType: unknown,
  displayname?: unknown
): boolean {
  if (isM98GlobalRhythmSyncCapableEquipment({ keyboardType, displayname })) {
    return false;
  }

  const kt = String(keyboardType ?? "").trim();
  if (kt && HFD_MUSIC_RHYTHM_24G_KEYBOARD_TYPES.has(kt)) return true;

  const dn = normalizeEquipmentName(displayname);
  return HFD_MUSIC_RHYTHM_DISPLAY_NAMES.has(dn);
}

/**
 * M98 / X75 Pro / DK108 等在 2.4G：子应用虚拟键盘只回显 V1，不走全局律动虚拟预览。
 * 实体灯效仍可由主应用全局桥下发；V1 由子应用桥 + musicDance 页负责。
 */
export function usesSubAppV1ExclusiveVirtualKeyboard(
  equipment: HfdRhythmEquipmentSnapshot | null | undefined
): boolean {
  if (!equipment) return false;
  const workMode = resolveEquipmentWorkMode(equipment);
  const keyboardType = String(equipment.keyboardType ?? "").trim();
  if (
    !isHfdMusicRhythm24GKeyboardModel(
      equipment.keyboardType,
      equipment.displayname ?? equipment.displayName
    ) ||
    !is24GWorkMode(workMode)
  ) {
    return false;
  }
  return shouldUseHfdMusicDataV1("huafenda", keyboardType, workMode);
}

/** 键盘子应用「音乐律动」菜单：M98 / X75 Pro 系列 / DK108 等 + 当前 2.4G */
export function supportsKeyboardSubAppMusicRhythm(
  equipment: HfdRhythmEquipmentSnapshot | null | undefined
): boolean {
  if (!equipment) return false;
  if (
    !isHfdMusicRhythm24GKeyboardModel(
      equipment.keyboardType,
      equipment.displayname ?? equipment.displayName
    )
  ) {
    return false;
  }
  return is24GWorkMode(resolveEquipmentWorkMode(equipment));
}

export function shouldUseHfdMusicDataV1(
  vendor: string | undefined,
  keyboardType: string | undefined,
  workMode: string | number | undefined,
  legacyHuafendaKeyboardTypes?: Set<string>
): boolean {
  const huafendaLike =
    vendor === "huafenda" ||
    (vendor === undefined &&
      keyboardType &&
      legacyHuafendaKeyboardTypes?.has(keyboardType));
  if (!huafendaLike) return false;
  if (keyboardType === "M98TFT" || keyboardType === "M98 8K") return false;
  if (!keyboardType || !HFD_MUSIC_V1_KEYBOARD_TYPES.has(keyboardType)) {
    return false;
  }
  return is24GWorkMode(workMode);
}

/**
 * 全局音乐律动列表：三款华奋达型号在 2.4G 下禁用（由键盘子应用 V1 独占，不迁入全局内页）。
 */
export function isHfdModelDisabledInGlobalMusicRhythmOn24G(
  equipment: HfdRhythmEquipmentSnapshot | null | undefined
): boolean {
  if (!equipment) return false;
  if (
    !isHfdMusicRhythm24GKeyboardModel(
      equipment.keyboardType,
      equipment.displayname ?? equipment.displayName
    )
  ) {
    return false;
  }
  return is24GWorkMode(resolveEquipmentWorkMode(equipment));
}

/** 华奋达 2.4G 接收器常见 PID（X87=0xfefe，X87 V2=0xfef9，X75-Pro 等=0xfefa） */
const HFD_24G_DONGLE_PRODUCT_IDS = new Set([0xfefe, 0xfef9, 0xfefa]);

function isHfd24GDongleProductId(pid: number): boolean {
  return HFD_24G_DONGLE_PRODUCT_IDS.has(pid);
}

function hidProductNameOf(hidDevice: unknown): string {
  const pn = (hidDevice as { productName?: unknown } | undefined)?.productName;
  return typeof pn === "string" ? pn.trim().toLowerCase() : "";
}

export function isHuafenda24GInterfaceRhythmTarget(
  equipment: Record<string, unknown>,
  hidDevice?: unknown
): boolean {
  const productName =
    hidProductNameOf(hidDevice) ||
    String(equipment.productName ?? "")
      .trim()
      .toLowerCase();
  const pid = Number(
    (hidDevice as { productId?: unknown } | undefined)?.productId ??
      equipment.productId
  );
  if (isHfd24GDongleProductId(pid)) return true;
  if (/2\.4g|dongle/.test(productName)) return true;
  const id = String(equipment.identifier ?? "").toLowerCase();
  if (id.includes("2.4g") && id.includes("dongle")) return true;
  if (productName === "muse-m98") return true;
  if (productName === "muse-m98-8k") return true;
  if (id.includes("muse-m98-8k") || id.includes("65276")) return true;
  if (pid === 0xfefc) return true;
  if (id.includes("65278") && !id.includes("32774")) return true;
  if (id.includes("65273")) return true;
  return is24GWorkMode(resolveEquipmentWorkMode(equipment));
}

export function isHuafendaWiredInterfaceRhythmTarget(
  equipment: Record<string, unknown>,
  hidDevice?: unknown
): boolean {
  if (isHuafenda24GInterfaceRhythmTarget(equipment, hidDevice)) return false;
  const pid = Number(
    (hidDevice as { productId?: unknown } | undefined)?.productId ??
      equipment.productId
  );
  /** X87 / SMART875 等为 0x8006；X87 V2 有线为 0x80bb；DK108 为 0x8801；X87 Neon 为 0x8804 */
  if (pid === 0x8006 || pid === 0x80bb || pid === 0x8801 || pid === 0x8804) {
    return true;
  }
  const wm = resolveEquipmentWorkMode(equipment);
  return wm === 0 || wm === "0";
}

function sameHfdRhythmProductLine(
  a: Record<string, unknown>,
  b: Record<string, unknown>
): boolean {
  const ka = canonicalKeyboardTypeForRhythmPolicy(a.keyboardType);
  const kb = canonicalKeyboardTypeForRhythmPolicy(b.keyboardType);
  return ka.length > 0 && ka === kb;
}

/**
 * 华奋达有线 / 2.4G 为同一物理键盘（如 X87 V2 USB 0x80bb 与接收器 0xfef9）：
 * 常规灯效 hfdRoutineEffect 快照按 keyboardType + 型号名共享。
 */
export function isHuafendaCrossInterfaceSamePhysicalKeyboard(
  left: Record<string, unknown> | null | undefined,
  right: Record<string, unknown> | null | undefined
): boolean {
  if (!left || !right) return false;
  if (String(left.solutionRovider ?? "") !== "huafenda") return false;
  if (String(right.solutionRovider ?? "") !== "huafenda") return false;
  if (!sameHfdRhythmProductLine(left, right)) return false;
  const na = normalizeEquipmentName(
    left.displayname ?? left.displayName ?? left.productName
  );
  const nb = normalizeEquipmentName(
    right.displayname ?? right.displayName ?? right.productName
  );
  if (na.length > 0 && na === nb) return true;

  const lRow = resolveHuafendaConfigRowByNameAndWorkMode(left);
  const rRow = resolveHuafendaConfigRowByNameAndWorkMode(right);
  if (lRow && rRow) {
    const lDn = normalizeEquipmentName(lRow.displayname ?? lRow.productName);
    const rDn = normalizeEquipmentName(rRow.displayname ?? rRow.productName);
    if (lDn.length > 0 && lDn === rDn) return true;
  }

  const lKt = resolveHuafendaKeyboardTypeFromNameBlob(left);
  const rKt = resolveHuafendaKeyboardTypeFromNameBlob(right);
  if (lKt && rKt && lKt === rKt) {
    const lIdent = normalizeEquipmentName(left.identifier);
    const rIdent = normalizeEquipmentName(right.identifier);
    const lIsNeonFef9 = isHfdFef9DongleX87NeonIdentifier(left.identifier);
    const rIsNeonFef9 = isHfdFef9DongleX87NeonIdentifier(right.identifier);
    const isNeonIdent = (ident: string, isNeonFef9: boolean) =>
      /\bx87\s*neon\b/.test(ident) || ident.includes("34820") || isNeonFef9;
    if (isNeonIdent(lIdent, lIsNeonFef9) && isNeonIdent(rIdent, rIsNeonFef9)) {
      return true;
    }
    if (
      lIdent.includes("x87 v2") &&
      (rIdent.includes("x87 v2") || (rIdent.includes("65273") && !rIsNeonFef9))
    ) {
      return true;
    }
    if (
      rIdent.includes("x87 v2") &&
      (lIdent.includes("x87 v2") || (lIdent.includes("65273") && !lIsNeonFef9))
    ) {
      return true;
    }
    const isClassicX87Ident = (ident: string) =>
      /\bx87\b/.test(ident) && !/\bx87\s*(?:v2|neon)\b/.test(ident);
    const lIsClassicX87 = isClassicX87Ident(lIdent);
    const rIsClassicX87 = isClassicX87Ident(rIdent);
    if (lIsClassicX87 && rIsClassicX87) return true;
    if (
      lIsClassicX87 &&
      (rIdent.includes("65278") || rIdent.includes("fefe"))
    ) {
      return true;
    }
    if (
      rIsClassicX87 &&
      (lIdent.includes("32774") || lIdent.includes("8006"))
    ) {
      return true;
    }
  }

  return false;
}

/** 华奋达 X87 V2 有线（USB 0x80bb / identifier 32955），与其它华奋达型号区分处理 */
export function isHuafendaX87V2WiredRhythmEquipment(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  if (!equipment || String(equipment.solutionRovider ?? "") !== "huafenda") {
    return false;
  }
  if (isHuafenda24GInterfaceRhythmTarget(equipment)) return false;
  const pid = Number(equipment.productId);
  if (pid === 0x80bb) return true;
  const ident = normalizeEquipmentName(equipment.identifier);
  if (ident.includes("32955") && ident.includes("x87 v2")) return true;
  const name = normalizeEquipmentName(
    equipment.displayname ?? equipment.displayName ?? equipment.productName
  );
  if (/\bx87\s*v2\b/i.test(name)) {
    const wm = resolveEquipmentWorkMode(equipment);
    return wm === 0 || wm === "0" || !is24GWorkMode(wm);
  }
  return false;
}

/** 华丰达 X75GC 有线（USB 0x8802/0x8803 / identifier 34818/34819） */
export function isHuafendaX75GcWiredRhythmEquipment(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  if (!equipment || String(equipment.solutionRovider ?? "") !== "huafenda") {
    return false;
  }
  if (isHuafenda24GInterfaceRhythmTarget(equipment)) return false;
  const pid = Number(equipment.productId);
  if (pid === 0x8802) return true;
  if (pid === 0x8803) {
    const identEarly = normalizeEquipmentName(equipment.identifier);
    if (identEarly.includes("x75gc")) return true;
    const nameEarly = normalizeEquipmentName(
      equipment.displayname ?? equipment.displayName ?? equipment.productName
    );
    if (/\bx75\s*gc\b/i.test(nameEarly)) return true;
    const ktEarly = String(equipment.keyboardType ?? "")
      .trim()
      .toLowerCase();
    if (ktEarly === "x75gc") return true;
    return false;
  }
  const kt = String(equipment.keyboardType ?? "")
    .trim()
    .toLowerCase();
  if (kt === "x75gc") {
    const wm = resolveEquipmentWorkMode(equipment);
    return wm === 0 || wm === "0" || !is24GWorkMode(wm);
  }
  const ident = normalizeEquipmentName(equipment.identifier);
  if (ident.includes("34818") && ident.includes("x75gc")) return true;
  if (ident.includes("34819") && ident.includes("x75gc")) return true;
  const name = normalizeEquipmentName(
    equipment.displayname ?? equipment.displayName ?? equipment.productName
  );
  if (/\bx75\s*gc\b/i.test(name)) {
    const wm = resolveEquipmentWorkMode(equipment);
    return wm === 0 || wm === "0" || !is24GWorkMode(wm);
  }
  return false;
}

/** 华奋达 SMART875 有线（USB 0x8006 / identifier 32774），与 X87 等同 PID 须按型号名区分 */
export function isHuafendaSmart875WiredRhythmEquipment(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  if (!equipment || String(equipment.solutionRovider ?? "") !== "huafenda") {
    return false;
  }
  if (isHuafenda24GInterfaceRhythmTarget(equipment)) return false;

  const name = normalizeEquipmentName(
    equipment.displayname ?? equipment.displayName ?? equipment.productName
  );
  const ident = normalizeEquipmentName(equipment.identifier);
  const kt = String(equipment.keyboardType ?? "")
    .trim()
    .toLowerCase();

  if (/\bsmart875\b/i.test(name)) {
    const wm = resolveEquipmentWorkMode(equipment);
    return wm === 0 || wm === "0" || !is24GWorkMode(wm);
  }
  if (ident.includes("smart875") && !ident.includes("65278")) {
    return true;
  }
  if (kt === "84" && Number(equipment.productId) === 0x8006) {
    return true;
  }
  return false;
}

/**
 * 华奋达有线律动退出：须 verify 后 setDevicesHuafenda 完整 reconnect（勿 2.4G）。
 * 当前含 X87 V2 有线、X75GC 有线、DK108 有线、SMART875 有线。
 */
export function isHuafendaDk108WiredRhythmEquipment(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  if (!equipment || String(equipment.solutionRovider ?? "") !== "huafenda") {
    return false;
  }
  if (isHuafenda24GInterfaceRhythmTarget(equipment)) return false;
  const pid = Number(equipment.productId);
  if (pid === 0x8801) return true;
  const kt = String(equipment.keyboardType ?? "").trim();
  if (kt === "DK108") {
    const wm = resolveEquipmentWorkMode(equipment);
    return wm === 0 || wm === "0" || !is24GWorkMode(wm);
  }
  const ident = normalizeEquipmentName(equipment.identifier);
  if (ident.includes("34817") && ident.includes("dk108")) return true;
  return false;
}

export function isHuafendaWiredRhythmQuitFullReconnectEquipment(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  return (
    isHuafendaX87V2WiredRhythmEquipment(equipment) ||
    isHuafendaX75GcWiredRhythmEquipment(equipment) ||
    isHuafendaDk108WiredRhythmEquipment(equipment) ||
    isHuafendaSmart875WiredRhythmEquipment(equipment)
  );
}

/**
 * 全局律动列表：2.4G 仅显示接收器行；有线仅显示 USB 行；其它主设备时隐藏有线幽灵项。
 */
export function shouldShowHuafendaRhythmTargetForActiveConnection(
  targetEquipment: Record<string, unknown>,
  activeEquipment: Record<string, unknown> | null | undefined,
  hidDevice?: unknown
): boolean {
  if (String(targetEquipment.solutionRovider ?? "") !== "huafenda") {
    return true;
  }
  if (
    !isHfdMusicRhythm24GKeyboardModel(
      targetEquipment.keyboardType,
      targetEquipment.displayname ?? targetEquipment.displayName
    )
  ) {
    return true;
  }

  const target24 = isHuafenda24GInterfaceRhythmTarget(
    targetEquipment,
    hidDevice
  );
  const targetWired = isHuafendaWiredInterfaceRhythmTarget(
    targetEquipment,
    hidDevice
  );

  if (!activeEquipment) {
    return !targetWired;
  }
  if (
    !isHfdMusicRhythm24GKeyboardModel(
      activeEquipment.keyboardType,
      activeEquipment.displayname ?? activeEquipment.displayName
    )
  ) {
    return !targetWired;
  }
  if (!sameHfdRhythmProductLine(targetEquipment, activeEquipment)) {
    return !targetWired;
  }

  const activeOn24 = is24GWorkMode(resolveEquipmentWorkMode(activeEquipment));
  if (activeOn24) return target24;
  return targetWired;
}

type HfdAuthorizedHid = {
  vendorId?: number;
  productId?: number;
  productName?: string;
};

/**
 * 从 getDevices 列表中为华奋达律动 target 解析应绑定的 HID。
 * 2.4G 时 equipmentData.productId 可能仍为有线 USB，须匹配接收器接口（0xfefe 等）。
 */
export function resolveHuafendaRhythmHidFromAuthorizedList(
  equipment: Record<string, unknown>,
  authorizedHids: unknown[],
  preferredHid?: unknown
): unknown | undefined {
  if (preferredHid) return preferredHid;
  const hids = authorizedHids as HfdAuthorizedHid[];
  if (!hids.length) return undefined;

  const vid = Number(equipment.vendorId);
  const pid = Number(equipment.productId);
  const on24G =
    is24GWorkMode(resolveEquipmentWorkMode(equipment)) ||
    isHuafenda24GInterfaceRhythmTarget(equipment);

  if (on24G) {
    const dongle = hids.find(
      (h) =>
        Number(h.vendorId) === vid &&
        isHuafenda24GInterfaceRhythmTarget(equipment, h)
    );
    if (dongle) return dongle;
  }

  const exact = hids.find(
    (h) => Number(h.vendorId) === vid && Number(h.productId) === pid
  );
  if (exact) {
    const exactIs24G = isHuafenda24GInterfaceRhythmTarget(equipment, exact);
    const exactIsWired = isHuafendaWiredInterfaceRhythmTarget(equipment, exact);
    if (on24G && !exactIs24G) {
      // equipment.productId 可能仍为有线 USB，勿在 2.4G 会话绑到有线 HID
    } else if (!on24G && exactIs24G && !exactIsWired) {
      // 有线会话跳过接收器 exact
    } else {
      return exact;
    }
  }

  if (!on24G) {
    const wired = hids.find(
      (h) =>
        Number(h.vendorId) === vid &&
        isHuafendaWiredInterfaceRhythmTarget(equipment, h)
    );
    if (wired) return wired;
  }

  return undefined;
}
